// src/components/dashboard/student/StudentSchedule.tsx
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  Video,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Loader,
} from "lucide-react";
import { mentorshipApi } from "../../../lib";

interface Session {
  _id: string;
  mentor: {
    _id: string;
    user?: {
      firstName?: string;
      lastName?: string;
      name?: string;
      avatar?: string;
    };
    specialty?: string;
  };
  topic: string;
  notes?: string;
  scheduledAt: string;
  duration: number;
  status: "pending" | "confirmed" | "completed" | "cancelled";
  meetingLink?: string;
}

const StudentSchedule = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await mentorshipApi.getMySessions({ role: "student", limit: 100 });
      if (response.success) {
        setSessions(response.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    } finally {
      setLoading(false);
    }
  };

  const getSessionsForDate = (date: Date) => {
    const dateStr = date.toISOString().split("T")[0];
    return sessions.filter(session => {
      const sessionDate = new Date(session.scheduledAt).toISOString().split("T")[0];
      return sessionDate === dateStr;
    });
  };

  const getMentorName = (mentor: Session['mentor']) => {
    if (mentor?.user?.firstName && mentor?.user?.lastName) {
      return `${mentor.user.firstName} ${mentor.user.lastName}`;
    }
    if (mentor?.user?.name) return mentor.user.name;
    return mentor?.specialty || "Mentor";
  };

  const getMentorAvatar = (mentor: Session['mentor']) => {
    return mentor?.user?.avatar;
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString("en-US", { 
      hour: "numeric", 
      minute: "2-digit" 
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-blue-100 text-blue-700";
      case "completed":
        return "bg-green-100 text-green-700";
      case "cancelled":
        return "bg-red-100 text-red-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: (Date | null)[] = [];

    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const changeMonth = (increment: number) => {
    setCurrentDate(
      new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + increment,
        1,
      ),
    );
  };

  const isToday = (date: Date | null) => {
    const todayDate = new Date();
    return date && date.toDateString() === todayDate.toDateString();
  };

  const isSelected = (date: Date | null) => {
    return date && date.toDateString() === selectedDate.toDateString();
  };

  const hasSessions = (date: Date | null) => {
    if (!date) return false;
    return getSessionsForDate(date).length > 0;
  };

  const selectedDateSessions = getSessionsForDate(selectedDate);
  const today = new Date();
  const todaySessions = getSessionsForDate(today).filter(s => s.status === "confirmed" || s.status === "pending");
  const upcomingSessions = sessions
    .filter(s => s.status === "confirmed" || s.status === "pending")
    .slice(0, 3);

  const days = getDaysInMonth(currentDate);
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">My Schedule</h1>
        <p className="mt-1 text-xs text-gray-500 sm:text-sm">
          Manage your mentorship sessions and events
        </p>
      </div>

      <div className="grid gap-4 xl:grid-cols-3 sm:gap-6">
        <div className="space-y-4 xl:col-span-2 sm:space-y-6">
          {/* Calendar */}
          <div className="p-3 bg-white border border-gray-200 rounded-xl sm:rounded-lg sm:p-4">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-sm font-semibold text-gray-900 sm:text-base">
                {currentDate.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}
              </h2>
              <div className="flex gap-1">
                <button
                  onClick={() => changeMonth(-1)}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={() => changeMonth(1)}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1 sm:mb-2">
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="text-center text-[10px] sm:text-xs font-medium text-gray-500 py-1.5 sm:py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
              {days.map((day, index) => (
                <button
                  key={index}
                  onClick={() => day && setSelectedDate(day)}
                  className={`
                    aspect-square sm:aspect-[1/1] p-0.5 sm:p-2 rounded-lg text-[10px] sm:text-xs font-medium transition-all
                    ${!day ? "invisible" : "hover:bg-gray-100"}
                    ${day && isSelected(day) ? "bg-primary text-white hover:bg-primary/90" : "text-gray-700"}
                    ${day && isToday(day) && !isSelected(day) ? "ring-2 ring-primary ring-inset" : ""}
                    ${day && hasSessions(day) && !isSelected(day) ? "bg-primary/5 font-semibold" : ""}
                  `}
                >
                  <div className="flex flex-col items-center justify-center h-full">
                    <span>{day?.getDate()}</span>
                    {day && hasSessions(day) && !isSelected(day) && (
                      <div className="w-1 h-1 bg-primary rounded-full mt-0.5" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Sessions for selected date */}
          <div className="p-3 bg-white border border-gray-200 rounded-xl sm:rounded-lg sm:p-4">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-sm font-semibold text-gray-900 sm:text-base">
                Sessions for{" "}
                {selectedDate.toLocaleDateString("default", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </h2>
              {selectedDateSessions.length === 0 && (
                <span className="text-xs text-gray-500">
                  No sessions
                </span>
              )}
            </div>

            <div className="space-y-3">
              {selectedDateSessions.length > 0 ? (
                selectedDateSessions.map((session) => (
                  <div
                    key={session._id}
                    className="flex items-start gap-3 p-3 transition rounded-lg bg-gray-50 hover:bg-gray-100"
                  >
                    <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 overflow-hidden rounded-full sm:w-12 sm:h-12 bg-primary/10">
                      {getMentorAvatar(session.mentor) ? (
                        <img 
                          src={getMentorAvatar(session.mentor)!} 
                          alt="" 
                          className="object-cover w-full h-full" 
                        />
                      ) : (
                        <Video className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">
                            {session.topic}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            with {getMentorName(session.mentor)}
                          </p>
                        </div>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${getStatusColor(session.status)}`}
                        >
                          {session.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-2 text-xs text-gray-500 sm:gap-3">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(session.scheduledAt)} ({session.duration} min)
                        </span>
                      </div>
                      {session.status === "confirmed" && session.meetingLink && (
                        <>
                          <button
                            onClick={() => navigate(`/video-room/${session._id}`)}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <Video className="w-3 h-3" />
                            Join In-App
                          </button>
                          <a
                            href={session.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 ml-2 text-xs text-gray-500 hover:underline"
                          >
                            Meet
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-6 text-center sm:py-8">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300 sm:w-10 sm:h-10" />
                  <p className="text-xs text-gray-500 sm:text-sm">No sessions scheduled for this day</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Today's Sessions */}
          <div className="p-3 bg-white border border-gray-200 rounded-xl sm:rounded-lg sm:p-4">
            <h2 className="mb-3 text-sm font-semibold text-gray-900 sm:text-base">
              Today's Sessions
            </h2>
            {todaySessions.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {todaySessions.map((session) => (
                  <div
                    key={session._id}
                    className="flex items-center gap-2 p-2 rounded-lg sm:gap-3 sm:p-3 bg-gradient-to-r from-orange-50 to-amber-50"
                  >
                    <Clock className="flex-shrink-0 w-4 h-4 text-orange-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate sm:text-sm">
                        {session.topic}
                      </p>
                      <p className="text-[10px] text-gray-500">
                        {formatTime(session.scheduledAt)}
                      </p>
                    </div>
                    {session.meetingLink && (
                      <>
                        <button
                          onClick={() => navigate(`/video-room/${session._id}`)}
                          className="px-2 sm:px-3 py-1.5 sm:py-2 bg-primary text-white text-[10px] sm:text-xs font-medium rounded-lg hover:bg-primary/90 flex-shrink-0"
                        >
                          Join In-App
                        </button>
                        <a
                          href={session.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-100 text-gray-700 text-[10px] sm:text-xs font-medium rounded-lg hover:bg-gray-200 flex-shrink-0 ml-1"
                        >
                          Meet
                        </a>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-4 text-center">
                <CheckCircle className="w-6 h-6 mx-auto mb-1 text-green-500" />
                <p className="text-xs text-gray-500">
                  No sessions today
                </p>
              </div>
            )}
          </div>

          {/* Upcoming Sessions */}
          <div className="p-3 bg-white border border-gray-200 rounded-xl sm:rounded-lg sm:p-4">
            <h2 className="mb-3 text-sm font-semibold text-gray-900 sm:text-base">
              Upcoming Sessions
            </h2>
            {upcomingSessions.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {upcomingSessions.map((session) => (
                  <div key={session._id} className="flex items-start gap-2 sm:gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-1.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate sm:text-sm">
                        {session.topic}
                      </p>
                      <p className="text-[10px] text-gray-500">
                        {new Date(session.scheduledAt).toLocaleDateString("default", {
                          month: "short",
                          day: "numeric",
                        })}{" "}
                        at {formatTime(session.scheduledAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-4 text-center">
                <Calendar className="w-6 h-6 mx-auto mb-1 text-gray-300" />
                <p className="text-xs text-gray-500">No upcoming sessions</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentSchedule;